/**
 * parent/onboarding.tsx — Magic-link landing + claim form (N-16).
 *
 * Lives at `/parent/onboarding?token=<jwt>`. The JWT itself is the
 * credential (no session required). On mount:
 *   1. parentMagicLinkStatusQuery validates the JWT and returns the
 *      parent profile + token status.
 *   2. If valid, render <ChildClaimForm>.
 *   3. If invalid (expired / used / malformed), show a friendly panel.
 */

import { createFileRoute, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import type { ReactNode } from 'react';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { StatePanel } from '../../components/ui/StatePanel.js';
import { ChildClaimForm } from '../../components/parent/ChildClaimForm.js';
import { parentMagicLinkStatusQuery } from '../../lib/api/queries.js';

const searchSchema = z.object({
  token: z.string().min(10).optional(),
});

export const Route = createFileRoute('/parent/onboarding')({
  validateSearch: (search) => searchSchema.parse(search),
  component: ParentOnboardingPage,
});

function ParentOnboardingPage(): ReactNode {
  const search = useSearch({ from: '/parent/onboarding' });
  const token = search.token ?? '';
  const status = useQuery(parentMagicLinkStatusQuery(token));

  if (!token) {
    return (
      <main className="page-shell">
        <PageHeader
          eyebrow="Onboarding orang tua"
          title="Tautan tidak lengkap"
          description="Tidak ada token undangan pada URL."
        />
        <StatePanel
          variant="error"
          title="Token tidak ditemukan"
          description="Pastikan Anda membuka link lengkap dari email undangan."
        />
      </main>
    );
  }

  if (status.isLoading) {
    return (
      <main className="page-shell">
        <p>Memvalidasi tautan undangan…</p>
      </main>
    );
  }

  if (!status.data?.ok) {
    const reason = (status.data as { error?: { code?: string } } | undefined)?.error?.code;
    return (
      <main className="page-shell">
        <PageHeader
          eyebrow="Onboarding orang tua"
          title="Tautan tidak valid"
          description="Tautan undangan Anda tidak dapat digunakan."
        />
        <StatePanel
          variant="error"
          title={
            reason === 'already_used'
              ? 'Tautan sudah pernah digunakan'
              : 'Tautan kedaluwarsa atau tidak valid'
          }
          description={
            reason === 'already_used'
              ? 'Jika Anda belum menyelesaikan klaim, hubungi admin sekolah untuk mengirim ulang undangan.'
              : 'Minta admin sekolah untuk mengirim ulang tautan undangan baru.'
          }
        />
      </main>
    );
  }

  const parentName = status.data.data.parent?.name;

  return (
    <main className="page-shell" data-testid="parent-onboarding">
      <PageHeader
        eyebrow="Onboarding orang tua"
        title={parentName ? `Halo, ${parentName}` : 'Onboarding orang tua'}
        description="Konfirmasi identitas anak Anda untuk mulai mengakses rapor progres belajar."
      />
      <section className="section">
        <ChildClaimForm token={token} parentName={parentName} />
      </section>
    </main>
  );
}